import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import SiteHeader from './SiteHeader';
import Footer from './Footer';
import useAuthModal from '../hooks/useAuthModal';
import '../styles/legalDoc.css';

/**
 * Renders a Markdown legal document with consistent styling and chrome
 * (SiteHeader, Footer, max-width container, prose).
 *
 * Pass `markdown` (string) — already imported via Vite `?raw`.
 *
 * The markdown content is the source of truth in `_docs/legal/`. Copies
 * live in `src/content/legal/` and are bundled via `?raw`. To update,
 * edit the source in `_docs/legal/` and re-copy into `src/content/legal/`.
 */
export default function LegalDoc({ markdown, title }) {
  const authModal = useAuthModal();
  const html = useMemo(() => {
    const rendered = marked.parse(markdown || '', { gfm: true, breaks: false });
    return DOMPurify.sanitize(rendered);
  }, [markdown]);

  return (
    <>
      <SiteHeader authModal={authModal} />
      <main className="bg-[#F0F0F0] min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <nav className="text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-gray-700">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-gray-700">{title}</span>
          </nav>
          <article
            className="legal-doc bg-white rounded-2xl shadow-sm p-6 sm:p-10"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </div>
      </main>
      <Footer />
    </>
  );
}
